/**
 * Vista de ACTIVACIÓN de cuenta del Promotor Digital (onboarding universal).
 *
 * El promotor nace SIN contraseña — la crea acá, en su primer acceso. Dos
 * variantes según cómo lo dio de alta su operador:
 *
 *   tieneCorreo=true  → el admin cargó su correo: solo "creá tu contraseña".
 *   tieneCorreo=false → autogestión: correo → código de 6 dígitos → contraseña
 *                       (el código y la contraseña se confirman JUNTOS al final).
 *
 * El dispositivoToken (canjeado del link) autoriza todo — no hay sesión aún.
 */

import ApiClient from '../api-client.js';
import { t } from '../languages.js';
import { traducirErrorDeEnvioDeCorreo } from '@shared/services/notificaciones/email-error-i18n.js';

const EMAIL_REGEX = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
const PASSWORD_MIN = 8;

// Mensaje de error de la respuesta `r` ({ok, error, mensaje}): si el fallo es de
// envío de correo (EMAIL_*), un mensaje claro y cálido (el genérico del backend
// "No se pudo enviar el correo" parecía un error de red/CORS); si no, el del
// backend o el i18n por defecto.
function _msgErrorEmail(r) {
    const msgCorreo = traducirErrorDeEnvioDeCorreo(r && r.error, t);
    if (msgCorreo) return msgCorreo;
    return (r && r.mensaje) || t('activacion.error');
}

export function renderActivacion({ estado, onActivada }) {
    const el = document.createElement('div');
    el.className = 'login-box';

    let conCorreoDelAdmin = !!estado.tieneCorreo;
    // Pasos: 'seleccion' (2+ cuentas en la caja: indicar cuál soy) → 'correo'
    // (autogestión: pedir código) → 'confirmar' (código + pass / solo pass).
    let paso = estado.multiplesPendientes ? 'seleccion' : conCorreoDelAdmin ? 'confirmar' : 'correo';
    let correoDeclarado = '';
    // Con 2+ pendientes: el correo que SELECCIONA la cuenta (viaja en cada llamada).
    let correoSeleccion = '';

    function render() {
        if (paso === 'seleccion') {
            el.innerHTML = `
                <div style="text-align:center; margin-bottom:8px;">
                    <h1 style="margin:0 0 4px;">${t('activacion.tituloBienvenida')}</h1>
                    <p style="color:var(--text-dim); margin:0;">${t('activacion.subtituloSeleccion')}</p>
                </div>
                <input type="email" id="act-sel-correo" placeholder="${t('activacion.tuCorreo')}"
                       autocomplete="email" inputmode="email">
                <div class="error" id="act-error"></div>
                <button class="btn btn-install" id="act-sel-continuar">${t('activacion.continuar')}</button>`;
            _bindPasoSeleccion();
        } else if (paso === 'correo') {
            el.innerHTML = `
                <div style="text-align:center; margin-bottom:8px;">
                    <h1 style="margin:0 0 4px;">${t('activacion.tituloBienvenida')}</h1>
                    <p style="color:var(--text-dim); margin:0;">${t('activacion.subtituloCorreo')}</p>
                </div>
                <input type="email" id="act-correo" placeholder="${t('activacion.tuCorreo')}"
                       autocomplete="email" inputmode="email" value="${correoDeclarado}">
                <div class="error" id="act-error"></div>
                <button class="btn btn-install" id="act-enviar">${t('activacion.enviarCodigo')}</button>`;
            _bindPasoCorreo();
        } else {
            el.innerHTML = `
                <div style="text-align:center; margin-bottom:8px;">
                    <h1 style="margin:0 0 4px;">${t('activacion.tituloBienvenida')}</h1>
                    <p style="color:var(--text-dim); margin:0;">
                        ${conCorreoDelAdmin ? t('activacion.subtituloPassword') : `${t('activacion.codigoEnviado')} <b>${correoDeclarado}</b>`}
                    </p>
                </div>
                ${conCorreoDelAdmin ? '' : `<input type="text" id="act-codigo" placeholder="${t('activacion.codigo')}" inputmode="numeric" autocomplete="one-time-code" maxlength="6">`}
                <input type="password" id="act-pass" placeholder="${t('activacion.password')}" autocomplete="new-password">
                <input type="password" id="act-pass2" placeholder="${t('activacion.passwordRepetir')}" autocomplete="new-password">
                <div class="error" id="act-error"></div>
                <button class="btn btn-install" id="act-crear">${t('activacion.crearCuenta')}</button>
                ${conCorreoDelAdmin ? '' : `<button class="btn" id="act-reenviar" style="margin-top:8px; background:transparent; color:var(--text-dim);">${t('activacion.reenviar')}</button>`}`;
            _bindPasoConfirmar();
        }
    }

    // ── Paso 0 (solo multi-pendiente): indicar MI correo para seleccionar cuenta ─
    function _bindPasoSeleccion() {
        const correoEl = el.querySelector('#act-sel-correo');
        const errEl = el.querySelector('#act-error');
        const btn = el.querySelector('#act-sel-continuar');

        let enProgreso = false;
        async function continuar() {
            if (enProgreso) return;
            const correo = (correoEl.value || '').trim().toLowerCase();
            if (!EMAIL_REGEX.test(correo)) {
                errEl.textContent = t('activacion.correoInvalido');
                return;
            }
            enProgreso = true;
            btn.disabled = true;
            errEl.textContent = '';
            try {
                const r = await ApiClient.activacionEstado(correo);
                if (!r.ok || !r.requiereActivacion || r.multiplesPendientes) {
                    // Sin match: el correo no corresponde a ninguna cuenta pendiente acá.
                    errEl.textContent = _msgErrorEmail(r);
                    return;
                }
                correoSeleccion = correo;
                conCorreoDelAdmin = !!r.tieneCorreo;
                paso = conCorreoDelAdmin ? 'confirmar' : 'correo';
                render();
            } finally {
                enProgreso = false;
                if (btn.isConnected) btn.disabled = false;
            }
        }
        btn.addEventListener('click', continuar);
        correoEl.addEventListener('keydown', e => {
            if (e.key === 'Enter') continuar();
        });
    }

    // ── Paso 1 (solo autogestión): declarar correo y pedir el código ─────
    function _bindPasoCorreo() {
        const correoEl = el.querySelector('#act-correo');
        const errEl = el.querySelector('#act-error');
        const btn = el.querySelector('#act-enviar');

        let enProgreso = false;
        async function enviar() {
            if (enProgreso) return;
            const correo = (correoEl.value || '').trim().toLowerCase();
            if (!EMAIL_REGEX.test(correo)) {
                errEl.textContent = t('activacion.correoInvalido');
                return;
            }
            enProgreso = true;
            btn.disabled = true;
            btn.textContent = t('activacion.enviando');
            errEl.textContent = '';
            try {
                const r = await ApiClient.activacionSolicitarCodigo(correo);
                if (!r.ok) {
                    errEl.textContent = _msgErrorEmail(r);
                    return;
                }
                correoDeclarado = correo;
                paso = 'confirmar';
                render();
            } finally {
                enProgreso = false;
                if (btn.isConnected) {
                    btn.disabled = false;
                    btn.textContent = t('activacion.enviarCodigo');
                }
            }
        }
        btn.addEventListener('click', enviar);
        correoEl.addEventListener('keydown', e => {
            if (e.key === 'Enter') enviar();
        });
    }

    // ── Paso 2: (código si autogestión) + contraseña doble → activar ─────
    function _bindPasoConfirmar() {
        const codigoEl = el.querySelector('#act-codigo'); // null si vino con correo del admin
        const passEl = el.querySelector('#act-pass');
        const pass2El = el.querySelector('#act-pass2');
        const errEl = el.querySelector('#act-error');
        const btn = el.querySelector('#act-crear');
        const reenviarBtn = el.querySelector('#act-reenviar');

        let enProgreso = false;
        async function activar() {
            if (enProgreso) return;
            const password = passEl.value || '';
            const passwordRepetida = pass2El.value || '';
            const codigo = codigoEl ? (codigoEl.value || '').trim() : null;

            if (codigoEl && !codigo) {
                errEl.textContent = t('activacion.codigoRequerido');
                return;
            }
            if (password.length < PASSWORD_MIN) {
                errEl.textContent = t('activacion.passwordCorta');
                return;
            }
            if (password !== passwordRepetida) {
                errEl.textContent = t('activacion.passwordNoCoincide');
                return;
            }

            enProgreso = true;
            btn.disabled = true;
            btn.textContent = t('activacion.creando');
            errEl.textContent = '';
            try {
                const r = await ApiClient.activacionCrearContrasena({
                    password,
                    passwordRepetida,
                    // Autogestión: correo + código (verificación). Variante (b) con
                    // multi-pendiente: solo el correo (selecciona MI cuenta).
                    ...(codigoEl ? { correo: correoDeclarado, codigo } : correoSeleccion ? { correo: correoSeleccion } : {}),
                });
                if (!r.ok) {
                    // Código irrecuperable (consumido por otra ejecución, expirado o
                    // agotado de intentos) → volver al paso del correo a pedir uno nuevo.
                    const CODIGOS_TERMINALES = ['codigo_consumido', 'EXPIRADO', 'AGOTADO', 'SIN_PENDIENTE'];
                    if (codigoEl && CODIGOS_TERMINALES.includes(r.error)) {
                        paso = 'correo';
                        render();
                        const err = el.querySelector('#act-error');
                        if (err) err.textContent = _msgErrorEmail(r);
                        return;
                    }
                    errEl.textContent = _msgErrorEmail(r);
                    return;
                }
                // Listo — el caller lleva al login con el email ya activado.
                onActivada(r.email);
            } finally {
                enProgreso = false;
                if (btn.isConnected) {
                    btn.disabled = false;
                    btn.textContent = t('activacion.crearCuenta');
                }
            }
        }

        btn.addEventListener('click', activar);
        pass2El.addEventListener('keydown', e => {
            if (e.key === 'Enter') activar();
        });

        if (reenviarBtn) {
            reenviarBtn.addEventListener('click', async () => {
                reenviarBtn.disabled = true;
                errEl.textContent = '';
                const r = await ApiClient.activacionSolicitarCodigo(correoDeclarado);
                if (!r.ok) errEl.textContent = _msgErrorEmail(r);
                // Cooldown visual simple: el backend enforza 60s; acá solo evitamos spam de taps.
                setTimeout(() => {
                    if (reenviarBtn.isConnected) reenviarBtn.disabled = false;
                }, 5000);
            });
        }
    }

    render();
    return el;
}
